import {useCallback, useState} from 'react';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {selectUser, useAppSelector} from '@/app/store';
import logError from '@/utils/errors/logError';

import {useLazyGetCardThemesV2Query} from './api';
import {CardThemesV2Model} from './entities';
import {getCardThemesV2Mapper} from './mapper';
import {useTrackers} from './trackers';

const ERROR_TITLE = "We couldn't load the card designs";
const ERROR_MESSAGE = "Let's give it another go!  Reload card designs.";

export const useCardThemeV2Repository = () => {
  //#region [--- [PROPERTIES] ---]
  const userData = useAppSelector(selectUser);
  //#endregion

  //#region [---- [APIS] ----]
  const [
    triggerGetCardThemesV2,
    {isLoading: isGetCardThemesV2Loading, isError: isGetCardThemesV2Error},
  ] = useLazyGetCardThemesV2Query();
  //#endregion

  //#region [---- [TRACK EVENTS] ----]
  const {
    trackApiErrorCardThemeListRetrieval,
    trackApiSuccessCardThemeListRetrieval,
  } = useTrackers();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [cardThemes, setCardThemes] = useState<CardThemesV2Model[]>(undefined);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handlerGetCardThemes = useCallback(async () => {
    try {
      const response = await triggerGetCardThemesV2().unwrap();
      if (response) {
        trackApiSuccessCardThemeListRetrieval(response, userData?.id);
        const responseMapped = getCardThemesV2Mapper(response);
        setCardThemes(responseMapped);
      }
    } catch (error) {
      trackApiErrorCardThemeListRetrieval(userData?.id, error);
      logError(error, {
        customTitle: 'Card Theme V2 - Error fetching card themes',
        fileName: 'useCardThemeV2Repository Cards theme',
        functionName: 'handlerGetCardThemes',
      });
      showSnackbar({
        summary: ERROR_TITLE,
        message: ERROR_MESSAGE,
      });
    }
  }, [
    trackApiErrorCardThemeListRetrieval,
    trackApiSuccessCardThemeListRetrieval,
    triggerGetCardThemesV2,
    userData?.id,
  ]);
  //#endregion

  return {
    cardThemes,
    isGetCardThemesV2Loading,
    isGetCardThemesV2Error,
    handlerGetCardThemes,
  };
};
